import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToggleSession, useSessionSummary } from '../../hooks/useSession';
import { useAuthStore } from '../../stores/auth.store';
import { formatCurrency } from '../../lib/currency';

interface CloseSessionProps {
  sessionId: string;
  onClosed: () => void;
}

export default function CloseSession({ sessionId, onClosed }: CloseSessionProps) {
  const { t } = useTranslation();
  const shop = useAuthStore((s) => s.shop);
  const currSym = shop?.curr_sym ?? '$';
  const toggleSession = useToggleSession();
  const { data: summary } = useSessionSummary(sessionId);

  const [closingCash, setClosingCash] = useState('');
  const [error, setError] = useState('');

  async function handleClose(e: React.FormEvent) {
    e.preventDefault();
    if (!shop?.id) return;
    setError('');

    try {
      await toggleSession.mutateAsync({
        shopid: shop.id,
        action: 'close',
        closing_cash: parseFloat(closingCash) || 0,
      });
      onClosed();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to close session');
    }
  }

  return (
    <div className="close-session-card">
      <h2 className="close-session-card__title">{t('closing_cash', 'Close Session')}</h2>

      {/* Session summary */}
      {summary && (
        <div className="session-summary">
          <div className="session-summary__row">
            <span>{t('total_sales', 'Total Sales')}</span>
            <span>{formatCurrency(summary.total_sales ?? 0, currSym)}</span>
          </div>
          <div className="session-summary__row">
            <span>Total Invoices</span>
            <span>{summary.total_invoices ?? 0}</span>
          </div>
          {summary.payment_breakdown &&
            Object.entries(summary.payment_breakdown).map(([type, amount]) => (
              <div key={type} className="session-summary__row session-summary__row--breakdown">
                <span>{type}</span>
                <span>{formatCurrency(Number(amount), currSym)}</span>
              </div>
            ))}
        </div>
      )}

      <form onSubmit={handleClose} className="session-close-form">
        <div className="form-group">
          <label className="form-label">{t('closing_cash', 'Closing Cash Balance')}</label>
          <input
            type="number"
            className="form-input form-input--lg"
            value={closingCash}
            onChange={(e) => setClosingCash(e.target.value)}
            placeholder="Enter actual cash count"
            step="0.01"
            min={0}
            autoFocus
          />
        </div>

        {error && <div className="alert alert--error">{error}</div>}

        <div className="form-actions">
          <button type="button" className="btn btn--outline" onClick={onClosed}>
            {t('cancel', 'Cancel')}
          </button>
          <button
            type="submit"
            className="btn btn--danger"
            disabled={toggleSession.isPending}
          >
            {toggleSession.isPending ? 'Closing...' : 'Confirm Close Session'}
          </button>
        </div>
      </form>
    </div>
  );
}
